import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '../lib/auth';
import { useLockStore } from '../store/useLockStore';
import { useTranslation } from '../lib/i18n';
import { Lock, Eye, EyeOff, LogOut, ShieldCheck, Loader2, Fingerprint, ScanFace } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import {
  isWebAuthnSupported,
  isPlatformAuthenticatorAvailable,
  hasBiometricCredentials,
  authenticateBiometric,
  registerBiometric,
} from '../lib/webauthn';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function IdleLockScreen() {
  const { lockedUser, unlock } = useLockStore();
  const { setTokens, fetchUser } = useAuthStore();
  const { t, language } = useTranslation();

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const inputRef = useRef(null);
  const clockRef = useRef(null);

  // Biometric state
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [hasBiometric, setHasBiometric] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [showBiometricSuggestion, setShowBiometricSuggestion] = useState(false);
  const biometricChecked = useRef(false);

  // Focus input + start clock
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 400);
    clockRef.current = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => { if (clockRef.current) clearInterval(clockRef.current); };
  }, []);

  // Check biometric availability and auto-trigger
  useEffect(() => {
    if (biometricChecked.current || !lockedUser?.email) return;
    biometricChecked.current = true;

    (async () => {
      const supported = isWebAuthnSupported() && await isPlatformAuthenticatorAvailable();
      setBiometricAvailable(supported);
      if (!supported) return;

      const hasCredential = await hasBiometricCredentials(lockedUser.email);
      setHasBiometric(hasCredential);

      if (hasCredential) {
        // Auto-trigger biometric prompt after a short delay
        setTimeout(() => handleBiometricUnlock(), 800);
      } else {
        // Show suggestion to register biometric
        const dismissed = localStorage.getItem('oms_biometric_dismissed');
        if (!dismissed) {
          setShowBiometricSuggestion(true);
        }
      }
    })();
  }, [lockedUser?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  // Block browser back/forward + keyboard shortcuts
  useEffect(() => {
    const blockNav = (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      window.history.pushState(null, '', window.location.href);
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', blockNav);

    const blockKeys = (e) => {
      if (e.target === inputRef.current) return;
      if (e.ctrlKey || e.altKey || e.metaKey) e.preventDefault();
    };
    window.addEventListener('keydown', blockKeys, true);

    return () => {
      window.removeEventListener('popstate', blockNav);
      window.removeEventListener('keydown', blockKeys, true);
    };
  }, []);

  // Cross-tab sync
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'oms_idle_lock' && e.newValue === null) {
        // Another tab unlocked - reload to restore session
        window.location.reload();
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!password.trim() || !lockedUser?.email) return;
    setLoading(true);
    setError('');
    try {
      // Call unlock endpoint (no auth token needed)
      const res = await axios.post(`${API_URL}/api/auth/unlock-session`, {
        email: lockedUser.email,
        password,
      });
      if (res.data.success) {
        const { access_token, refresh_token } = res.data.data;
        // Restore session with new tokens
        setTokens(access_token, refresh_token);
        await fetchUser();
        unlock();
        toast.success(t('lockScreen.unlockSession'));
      }
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      setError(detail === 'AUTH_INVALID_PASSWORD' ? t('lockScreen.wrongPassword') : (t('lockScreen.wrongPassword')));
      setShake(true);
      setTimeout(() => setShake(false), 600);
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    unlock();
    useAuthStore.getState().logout();
    window.location.href = '/login';
  };

  const handleBiometricUnlock = useCallback(async () => {
    if (!lockedUser?.email || biometricLoading) return;
    setBiometricLoading(true);
    setError('');
    try {
      const result = await authenticateBiometric(lockedUser.email);
      if (result.success) {
        const { access_token, refresh_token } = result.data;
        setTokens(access_token, refresh_token);
        await fetchUser();
        unlock();
        toast.success(t('lockScreen.unlockSession'));
      }
    } catch (err) {
      // User cancelled or biometric failed - silently fall back to password
      if (err.name !== 'NotAllowedError') {
        setError(t('lockScreen.biometricFailed'));
      }
    } finally {
      setBiometricLoading(false);
    }
  }, [lockedUser?.email, biometricLoading, setTokens, fetchUser, unlock, t]);

  const dismissBiometricSuggestion = () => {
    setShowBiometricSuggestion(false);
    localStorage.setItem('oms_biometric_dismissed', '1');
  };

  const initials = lockedUser?.full_name
    ? lockedUser.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '??';

  const localeMap = { tr: 'tr-TR', en: 'en-GB', nl: 'nl-NL', fr: 'fr-FR' };
  const locale = localeMap[language] || 'tr-TR';
  const timeStr = currentTime.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  const dateStr = currentTime.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden select-none"
      data-testid="idle-lock-screen"
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Deep dark background */}
      <div className="absolute inset-0 bg-[#0a0e1a]" />

      {/* Animated grid */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: `linear-gradient(rgba(59,130,246,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.5) 1px, transparent 1px)`,
        backgroundSize: '60px 60px',
        animation: 'grid-drift 20s linear infinite'
      }} />

      {/* Radial blue glow */}
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 600px 400px at 50% 45%, rgba(59,130,246,0.12) 0%, rgba(14,165,233,0.05) 40%, transparent 70%)'
      }} />

      {/* Floating orbs */}
      <div className="absolute w-[300px] h-[300px] rounded-full" style={{
        background: 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)',
        top: '10%', left: '15%',
        animation: 'orb-float 8s ease-in-out infinite'
      }} />
      <div className="absolute w-[200px] h-[200px] rounded-full" style={{
        background: 'radial-gradient(circle, rgba(14,165,233,0.06) 0%, transparent 70%)',
        bottom: '15%', right: '10%',
        animation: 'orb-float 10s ease-in-out infinite reverse'
      }} />

      {/* Scan lines */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(59,130,246,0.015) 2px, rgba(59,130,246,0.015) 4px)'
      }} />

      {/* Content */}
      <div className={`relative z-10 w-full max-w-md mx-4 animate-in zoom-in-95 slide-in-from-bottom-6 duration-700 ${shake ? 'animate-shake' : ''}`}>

        {/* Clock */}
        <div className="text-center mb-8 animate-in fade-in slide-in-from-top-4 duration-700 delay-200">
          <div className="text-6xl font-extralight text-white/90 tracking-wider font-mono" style={{ textShadow: '0 0 40px rgba(59,130,246,0.3)' }}>
            {timeStr}
          </div>
          <div className="text-sm text-blue-300/50 mt-2 tracking-widest uppercase font-light">
            {dateStr}
          </div>
        </div>

        {/* Card */}
        <div className="relative rounded-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300">
          <div className="absolute inset-0 rounded-2xl p-[1px]">
            <div className="absolute inset-0 rounded-2xl" style={{
              background: 'linear-gradient(135deg, rgba(59,130,246,0.3) 0%, rgba(14,165,233,0.1) 50%, rgba(99,102,241,0.3) 100%)'
            }} />
          </div>

          <div className="relative m-[1px] rounded-2xl bg-[#0f1629]/95 backdrop-blur-xl p-8">
            {/* Fingerprint icon */}
            <div className="flex justify-center mb-5">
              <div className="relative">
                <div className="absolute inset-[-8px] rounded-full border border-blue-500/20 animate-[pulse-ring_3s_ease-in-out_infinite]" />
                <div className="absolute inset-[-16px] rounded-full border border-blue-500/10 animate-[pulse-ring_3s_ease-in-out_infinite_0.5s]" />
                <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 flex items-center justify-center">
                  <Fingerprint className="h-8 w-8 text-blue-400" style={{ filter: 'drop-shadow(0 0 8px rgba(59,130,246,0.5))' }} />
                </div>
              </div>
            </div>

            {/* User info */}
            <div className="text-center mb-6">
              <div className="mx-auto w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-semibold text-sm mb-3 shadow-lg shadow-blue-500/25 border border-blue-400/30">
                {initials}
              </div>
              <h2 className="text-lg font-medium text-white/90 tracking-wide">{lockedUser?.full_name}</h2>
              <div className="flex items-center justify-center gap-2 mt-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                <p className="text-xs text-blue-300/60 tracking-wider uppercase">{t('lockScreen.sessionLockedDesc')}</p>
              </div>
            </div>

            <div className="h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent mb-6" />

            {/* Password form */}
            <form onSubmit={handleUnlock} className="space-y-4">
              <div className="relative group">
                <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-blue-500/20 via-cyan-500/20 to-blue-500/20 opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 blur-sm" />
                <div className="relative">
                  <Input
                    ref={inputRef}
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    placeholder={t('lockScreen.authPlaceholder')}
                    className="h-12 bg-white/[0.03] border-white/[0.08] text-white placeholder:text-blue-300/30 pr-12 focus:border-blue-500/40 focus:ring-blue-500/20 font-mono tracking-wider text-sm"
                    data-testid="idle-lock-password"
                    autoComplete="current-password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-400/40 hover:text-blue-300 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-400/90 text-sm justify-center animate-in fade-in slide-in-from-top-1 duration-200">
                  <div className="w-1 h-1 rounded-full bg-red-400" />
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || !password.trim()}
                className="w-full h-11 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-medium tracking-wide border-0 shadow-lg shadow-blue-500/20 transition-all duration-300 hover:shadow-blue-500/40 hover:scale-[1.01]"
                data-testid="idle-lock-unlock-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <ShieldCheck className="h-4 w-4 mr-2" />
                )}
                {t('lockScreen.unlock')}
              </Button>
            </form>

            {/* Biometric unlock button */}
            {biometricAvailable && hasBiometric && (
              <>
                <div className="flex items-center gap-3 my-4">
                  <div className="flex-1 h-px bg-white/[0.05]" />
                  <span className="text-[10px] text-blue-300/30 tracking-widest uppercase">{t('lockScreen.or')}</span>
                  <div className="flex-1 h-px bg-white/[0.05]" />
                </div>
                <Button
                  onClick={handleBiometricUnlock}
                  disabled={biometricLoading}
                  className="w-full h-11 bg-gradient-to-r from-emerald-600/80 to-teal-600/80 hover:from-emerald-500 hover:to-teal-500 text-white font-medium tracking-wide border border-emerald-500/20 shadow-lg shadow-emerald-500/10 transition-all duration-300 hover:shadow-emerald-500/30 hover:scale-[1.01]"
                  data-testid="idle-lock-biometric-btn"
                >
                  {biometricLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <ScanFace className="h-4 w-4 mr-2" />
                  )}
                  {t('lockScreen.biometricUnlock')}
                </Button>
              </>
            )}

            {/* Biometric registration suggestion */}
            {biometricAvailable && !hasBiometric && showBiometricSuggestion && (
              <div className="mt-4 p-3 rounded-lg bg-emerald-500/[0.06] border border-emerald-500/15 animate-in fade-in slide-in-from-bottom-2 duration-500">
                <div className="flex items-start gap-2.5">
                  <ScanFace className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-emerald-300/70 leading-relaxed">
                      {t('lockScreen.biometricSuggestion')}
                    </p>
                    <button
                      onClick={dismissBiometricSuggestion}
                      className="text-[10px] text-emerald-400/40 hover:text-emerald-300/60 mt-1.5 transition-colors"
                    >
                      {t('lockScreen.dismiss')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-white/[0.05]" />
              <span className="text-[10px] text-red-300/30 tracking-widest uppercase">{t('lockScreen.or')}</span>
              <div className="flex-1 h-px bg-white/[0.05]" />
            </div>

            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full text-blue-300/40 hover:text-red-400 hover:bg-red-500/5 transition-all duration-300 tracking-wide text-sm"
              data-testid="idle-lock-logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              {t('lockScreen.logout')}
            </Button>
          </div>
        </div>

        {/* Status bar */}
        <div className="flex items-center justify-center gap-4 mt-6 animate-in fade-in duration-700 delay-500">
          <div className="flex items-center gap-1.5">
            <Lock className="h-3 w-3 text-blue-500/40" />
            <span className="text-[11px] text-blue-300/30 tracking-wider">
              {t('lockScreen.inactivityShort', { minutes: lockedUser?.timeoutMinutes || 3 })}
            </span>
          </div>
          <div className="w-1 h-1 rounded-full bg-blue-500/20" />
          <span className="text-[11px] text-blue-300/30 tracking-wider">
            OMS Portal
          </span>
        </div>
      </div>

      <style>{`
        @keyframes grid-drift {
          0% { transform: translate(0, 0); }
          100% { transform: translate(60px, 60px); }
        }
        @keyframes orb-float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(30px, -20px) scale(1.1); }
        }
        @keyframes pulse-ring {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.15); opacity: 0; }
        }
        .animate-shake {
          animation: shake 0.6s cubic-bezier(.36,.07,.19,.97) both;
        }
        @keyframes shake {
          10%, 90% { transform: translateX(-2px); }
          20%, 80% { transform: translateX(4px); }
          30%, 50%, 70% { transform: translateX(-6px); }
          40%, 60% { transform: translateX(6px); }
        }
      `}</style>
    </div>
  );
}
